import { PreflightHolder, PreflightLastArrival, PreflightOrder } from './document-preflight';

/**
 * Links from the availability pre-flight modal to the documents it names (owner, 2026-10-25: "a link to every mentioned document
 * in the modal, like the document selector's leftmost button": open the document in a new tab). The editor route is chosen by
 * `DOCTYPE` (`edit-document/10/:id` is the purchase-order editor, `/2` and `/3` the receipt editor, any other type the generic one),
 * so a link needs the type as well as the id. Pure: no router, no window; the component opens the target.
 */
export interface PreflightDocumentTarget {
  /** `Documents.DOCTYPE`: picks the editor. */
  type: number;
  /** `Documents.DOCID`. */
  id: number;
}

/** A holder is an invoice: the reservation and sold figures the booking list is built from read invoices only. */
export const PREFLIGHT_DOCTYPE_INVOICE = 1;
/** A receipt. The receipt editor serves types 2 and 3 alike (`app-routing.module.ts`), so it is the safe default. */
export const PREFLIGHT_DOCTYPE_RECEIPT = 2;
/** A purchase order: the in-transit list reads `DOCTYPE 10` and nothing else. */
export const PREFLIGHT_DOCTYPE_PURCHASE_ORDER = 10;

/** The tooltip of every link button. */
export const PREFLIGHT_OPEN_DOCUMENT_TOOLTIP = 'Відкрити документ у новій вкладці';

const positiveInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0;

/** A target for a usable id and type, otherwise null: a document that cannot be identified gets no button, never a wrong link. */
export function preflightTarget(type: unknown, id: unknown): PreflightDocumentTarget | null {
  return positiveInt(id) && positiveInt(type) ? { type, id } : null;
}

/** The holding invoice. An API older than `ipf-4.3` sends no `docType`, and a document the API could not find sends null: both mean an invoice. */
export function preflightHolderTarget(h: Pick<PreflightHolder, 'docId' | 'docType'>): PreflightDocumentTarget | null {
  return preflightTarget(positiveInt(h.docType) ? h.docType : PREFLIGHT_DOCTYPE_INVOICE, h.docId);
}

/** A queued purchase order. */
export function preflightOrderTarget(o: Pick<PreflightOrder, 'docId'>): PreflightDocumentTarget | null {
  return preflightTarget(PREFLIGHT_DOCTYPE_PURCHASE_ORDER, o.docId);
}

/** The receipt of the last arrival. An API older than `ipf-4.3` sends no `receiptDocType`: the receipt editor serves 2 and 3 alike. */
export function preflightReceiptTarget(la: Pick<PreflightLastArrival, 'receiptDocId' | 'receiptDocType'>): PreflightDocumentTarget | null {
  return preflightTarget(positiveInt(la.receiptDocType) ? la.receiptDocType : PREFLIGHT_DOCTYPE_RECEIPT, la.receiptDocId);
}

/** The purchase order the last receipt was booked against. */
export function preflightReceiptOrderTarget(la: Pick<PreflightLastArrival, 'orderDocId'>): PreflightDocumentTarget | null {
  return preflightTarget(PREFLIGHT_DOCTYPE_PURCHASE_ORDER, la.orderDocId);
}

/** The router commands of a target: the same path the document selector opens (`['/edit-document', DOCTYPE, DOCID]`). */
export function preflightDocumentCommands(t: PreflightDocumentTarget): (string | number)[] {
  return ['/edit-document', t.type, t.id];
}

/** One link button of the last-arrival line: its label and where it goes. */
export interface PreflightArrivalLink {
  label: string;
  target: PreflightDocumentTarget;
}

const RECEIPT_LABEL: Record<number, string> = { 2: 'Прихідна накладна', 3: 'Накладна на імпорт' };

/**
 * The links of the last-arrival line: the receipt and the purchase order it was booked against, each named by its kind and number.
 * Nothing when there is no last arrival (null, or an API older than `ipf-4.2`).
 */
export function preflightLastArrivalLinks(la: PreflightLastArrival | null | undefined): PreflightArrivalLink[] {
  if (!la) { return []; }
  const links: PreflightArrivalLink[] = [];
  const receipt = preflightReceiptTarget(la);
  if (receipt) {
    links.push({ label: (RECEIPT_LABEL[receipt.type] || 'Надходження') + (la.receiptDocNumber ? ' ' + la.receiptDocNumber : ''), target: receipt });
  }
  const order = preflightReceiptOrderTarget(la);
  if (order) {
    links.push({ label: 'Замовлення постачальнику' + (la.orderDocNumber ? ' ' + la.orderDocNumber : ''), target: order });
  }
  return links;
}
